import type { MessagingInstanceDto } from '@kardume/types';

/**
 * Conversões e validação do agendamento de campanha.
 *
 * Compartilhado entre o modal de criação e o drawer de detalhe porque as duas telas
 * precisam recusar exatamente o mesmo horário. Duas cópias da regra garantiriam que
 * uma delas deixaria passar o que a outra barra, e o operador aprenderia a
 * desconfiar do aviso.
 */

/** Só a janela e o fim de semana importam aqui - o resto da instância é irrelevante. */
type SendWindow = Pick<MessagingInstanceDto, 'windowStart' | 'windowEnd' | 'sendOnWeekends'>;

/** `HH:mm:ss` do backend vira `HH:mm` na tela. */
export const shortTime = (value: string): string => value.slice(0, 5);

const toMinutes = (value: string): number => {
  const [hours, minutes] = value.split(':');
  return Number(hours) * 60 + Number(minutes);
};

/**
 * ISO do backend → valor de um `<input type="datetime-local">`.
 *
 * O input não aceita offset nem segundos, e uma string com eles simplesmente não
 * aparece no campo - sem erro, sem aviso, campo vazio. Por isso a conversão passa
 * pelos getters locais do Date em vez de um `slice` na string ISO, que traria a hora
 * em UTC.
 */
export const toLocalInputValue = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number): string => String(value).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

/** Valor do input (hora local, sem fuso) → ISO com offset, que é o que o backend lê. */
export const toIsoFromInput = (value: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/**
 * Devolve o motivo pelo qual este horário não serve, ou `null` quando serve.
 *
 * Espelha o `validateSchedule` do backend, que é quem manda - aqui a checagem existe
 * só para o operador descobrir o problema antes de salvar, em vez de por um 400.
 *
 * Avaliada no fuso do navegador. Para o time comercial, que trabalha no horário de
 * Brasília, é o mesmo fuso do negócio; num navegador configurado em outro fuso o
 * aviso pode divergir, e aí quem decide é o backend.
 */
export const describeScheduleProblem = (
  inputValue: string,
  instance: SendWindow | null | undefined
): string | null => {
  if (!inputValue) return null;
  const when = new Date(inputValue);
  if (Number.isNaN(when.getTime())) return 'Data inválida.';
  if (when.getTime() <= Date.now()) return 'A data de início precisa estar no futuro.';
  if (!instance) return null;

  const weekday = when.getDay();
  if (!instance.sendOnWeekends && (weekday === 0 || weekday === 6)) {
    return 'Esta instância não envia em fins de semana. Escolha um dia útil.';
  }

  const minutes = when.getHours() * 60 + when.getMinutes();
  if (minutes < toMinutes(instance.windowStart) || minutes >= toMinutes(instance.windowEnd)) {
    return (
      'Fora da janela de envio da instância ' +
      `(${shortTime(instance.windowStart)}–${shortTime(instance.windowEnd)}).`
    );
  }
  return null;
};
